// src/utils/formatDate.js
import {
  format,
  parse,
  differenceInYears,
  differenceInMonths,
  differenceInDays,
  subMonths,
  subYears,
} from "date-fns"

/**
 * Formats an ISO date string into a readable format.
 * Automatically converts from UTC to the user's local time zone.
 * @param {string | null | undefined} dateString - The ISO 8601 date string.
 * @param {string} formatStyle - The desired output format (e.g., 'dd MMM yyyy').
 * @returns {string} The formatted date string, or an empty string if the input is invalid.
 */
export const formatReadableDate = (
  dateString,
  formatStyle = "EEE, MMM d, y h:mm a"
) => {
  if (!dateString) {
    return "-" // Or return an empty string, based on your preference
  }

  try {
    const date = new Date(dateString)
    return format(date, formatStyle)
  } catch (error) {
    console.error("Invalid date string provided:", dateString)
    return "Invalid Date"
  }
}

/**
 * Formats "HH:mm" or "HH:mm:ss" to "hh:mm AM/PM".
 */
export const formatReadableTime = timeString => {
  if (!timeString) return "-"

  try {
    const parsed = parse(timeString, "HH:mm", new Date())
    return format(parsed, "hh:mm a") // Example: 10:00 → 10:00 AM
  } catch {
    return "-"
  }
}

/**
 * Format an ISO date-time string in a specific IANA timezone using date-fns `format`.
 * Use this for times that must display in the server/sales-timezone rather than the
 * user's browser local time.
 *
 * @param {string|Date|null|undefined} dateInput
 * @param {string} timeZone  e.g. "Asia/Dhaka"
 * @param {string} formatStyle  date-fns format string (default: "hh:mm a")
 * @returns {string}
 */
export const formatReadableTimeInZone = (
  dateInput,
  timeZone,
  formatStyle = "hh:mm a"
) => {
  if (!dateInput) return "-"

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput)
  if (Number.isNaN(date.getTime())) return "-"

  try {
    const includeSeconds = formatStyle.includes("ss")
    const hour12 = formatStyle.includes("a")
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone || undefined,
      hour: "2-digit",
      minute: "2-digit",
      second: includeSeconds ? "2-digit" : undefined,
      hour12,
    }).formatToParts(date)

    const get = type => parts.find(p => p.type === type)?.value ?? ""
    const hour = get("hour")
    const minute = get("minute")
    const second = get("second")
    const ampm = get("dayPeriod")

    if (formatStyle === "hh:mm a") {
      return `${hour}:${minute} ${ampm}`
    }
    if (formatStyle === "hh:mm:ss a") {
      return `${hour}:${minute}:${second} ${ampm}`
    }

    return format(date, formatStyle)
  } catch (error) {
    console.error("Invalid timezone for formatReadableTimeInZone:", timeZone, error)
    return "-"
  }
}

/**
 * Format a date-only string ("YYYY-MM-DD") in a specific timezone, avoiding the
 * off-by-one-day shift that happens when `new Date("2026-06-13")` is parsed as
 * UTC and then displayed in a negative-offset browser timezone.
 */
export const formatReadableDateInZone = (
  dateInput,
  timeZone,
  formatStyle = "MMM d, yyyy"
) => {
  if (!dateInput) return "-"

  try {
    const raw = String(dateInput)
    const base = raw.includes("T") ? raw : `${raw.slice(0, 10)}T12:00:00Z`
    return format(new Date(base), formatStyle)
  } catch (error) {
    console.error("Invalid date for formatReadableDateInZone:", dateInput, error)
    return "-"
  }
}

/**
 * Render a minute count as a compact "Xh Ym" / "Ym" string.
 */
export const formatMinutes = totalMinutes => {
  const m = Number(totalMinutes)
  if (!Number.isFinite(m) || m <= 0) return "—"
  const hours = Math.floor(m / 60)
  const mins = m % 60
  if (hours === 0) return `${mins}m`
  if (mins === 0) return `${hours}h`
  return `${hours}h ${mins}m`
}

/**
 * Render a duration in seconds as a human-readable string.
 * Drops zero units; never shows fractional minutes.
 *   26     → "26s"
 *   90     → "1m 30s"
 *   3060   → "51m"
 *   30615  → "8h 30m"
 *   90061  → "1d 1h 1m"
 */
export const formatDuration = totalSeconds => {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0))
  if (s === 0) return "0s"
  const days = Math.floor(s / 86400)
  const hours = Math.floor((s % 86400) / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  const seconds = s % 60
  const parts = []
  if (days > 0) parts.push(`${days}d`)
  if (hours > 0) parts.push(`${hours}h`)
  if (minutes > 0) parts.push(`${minutes}m`)
  if (seconds > 0 && hours === 0 && days === 0) parts.push(`${seconds}s`)
  return parts.join(" ") || "0s"
}

/**
 * Calculates how long ago the given date was, returning years, months, and days.
 * Example: "2 years, 3 months, 5 days ago"
 * @param {string | Date} dateInput - The input date (string or Date object)
 * @returns {string} The formatted duration since that date.
 */
export const formatSinceDate = (dateInput, nowInput = new Date()) => {
  if (!dateInput) return "-"

  try {
    const start = new Date(dateInput)
    const now = nowInput

    let years = differenceInYears(now, start)
    let tempDate = subYears(now, years)

    let months = differenceInMonths(tempDate, start)
    tempDate = subMonths(tempDate, months)

    let days = differenceInDays(tempDate, start)

    const parts = []
    if (years > 0) parts.push(`${years} year${years > 1 ? "s" : ""}`)
    if (months > 0) parts.push(`${months} month${months > 1 ? "s" : ""}`)
    if (days > 0) parts.push(`${days} day${days > 1 ? "s" : ""}`)

    return parts.length > 0 ? parts.join(", ") : "Today"
  } catch (error) {
    console.error("Error calculating time since date:", dateInput, error)
    return "-"
  }
}

export const toLocalDatetimeInput = (date = new Date()) => {
  const pad = n => n.toString().padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}
